import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import type { IMfaStatusView } from '@shared/services';
import { useStepUp } from './useStepUp';
import { addEmailStart, addEmailVerify } from '@/shared/api/mfa';
import { getLoginFlowError } from '@/shared/utils';

/**
 * The wizard position for in-page email setup:
 * - `idle`   — no setup in progress; the card shows the `Set up` action.
 * - `stepup` — collecting the current password before starting enrollment (which sends the code).
 * - `verify` — accepting the emailed one-time code.
 */
type EmailStep = 'idle' | 'stepup' | 'verify';

/**
 * Session-authorized email one-time-code setup, mirroring `useTotpSetup`. The step-up start call
 * (`addEmailStart`) both re-authenticates and sends the code to the account email, after which the
 * user enters it. Revalidates the shared `useMfaStatus` cache on success. No disable path exists, and
 * because the password is never retained past the start call there is no in-place resend — the user
 * cancels and restarts to get a fresh code.
 */
export const useEmailSetup = (mutate: KeyedMutator<IMfaStatusView>) => {
  const stepUp = useStepUp();
  const [step, setStep] = useState<EmailStep>('idle');
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const open = () => setStep('stepup');

  const close = () => {
    setStep('idle');
    setCode('');
    setVerifyError(null);
    stepUp.reset();
  };

  // Step-up → start sends the code; a rejected password stays inline in the prompt.
  const submitStepUp = async () => {
    const result = await stepUp.runStepUp((currentPassword) => addEmailStart({ currentPassword }));

    if (result.ok) {
      setStep('verify');
    }
  };

  // Verify the emailed code. On success the factor is enrolled — revalidate and close; on failure
  // keep the step open with an inline error.
  const submitCode = async () => {
    setVerifyError(null);
    setIsVerifying(true);

    try {
      await addEmailVerify({ code });
      await mutate();
      close();
    } catch (caught) {
      setVerifyError(getLoginFlowError(caught));
    } finally {
      setIsVerifying(false);
    }
  };

  return {
    step,
    open,
    close,
    stepUp,
    submitStepUp,
    code,
    setCode,
    submitCode,
    isVerifying,
    verifyError,
  };
};
